import { MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatDateTime, formatDuration } from '../../lib/formatters'
import { cn } from '../../lib/utils'
import { useActivityTranscript, useDetachCallActivity, useRecordingUrl } from '../../models/aircall'
import { type SalesLeadActivity, SalesLeadActivityType } from '../../models/salesLead'
import { ActivityAuthorIcon } from '../../routes/Private/App/Internal/SalesLeads/ActivityAuthorIcon'
import { activityTypeStyles } from '../../routes/Private/App/Internal/SalesLeads/activityTypeStyles'

interface Props {
  leadId: string
  activity: SalesLeadActivity
}

function isMissed(type: SalesLeadActivityType): boolean {
  return (
    type === SalesLeadActivityType.CALL_INBOUND_MISSED || type === SalesLeadActivityType.CALL_OUTBOUND_MISSED
  )
}

function RecordingBlock({ activity }: { activity: SalesLeadActivity }) {
  const status = activity.recordingStatus
  const query = useRecordingUrl(activity.id, status === 'stored')

  if (!status || status === 'none') return null
  if (status === 'pending') {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <div className="h-2 w-32 animate-pulse rounded bg-gray-200" />
        Recording processing
      </div>
    )
  }
  if (status === 'failed') {
    return <p className="text-muted-foreground text-sm">Recording unavailable</p>
  }
  if (query.isLoading) {
    return <p className="text-muted-foreground text-sm">Loading recording…</p>
  }
  if (query.isError || !query.data?.url) {
    return <p className="text-muted-foreground text-sm">Recording could not be loaded</p>
  }
  return (
    <>
      <audio controls preload="metadata" src={query.data.url} className="audio-player h-10 w-full" />

      <style>{`
       .audio-player::-webkit-media-controls-enclosure {
          border-radius: 3px !important;
          background-color: var(--background) !important;
        }
      `}</style>
    </>
  )
}

function TranscriptBlock({ activityId, status }: { activityId: string; status: string | null | undefined }) {
  const [showTranscript, setShowTranscript] = useState(false)
  const query = useActivityTranscript(activityId, showTranscript)

  if (!status || status === 'none') return null
  if (status === 'pending') {
    return <p className="text-muted-foreground text-sm">Transcript processing…</p>
  }
  if (status === 'failed') {
    return <p className="text-muted-foreground text-sm">Transcript unavailable</p>
  }

  if (!showTranscript) {
    return (
      <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowTranscript(true)}>
        Show transcript
      </Button>
    )
  }
  if (query.isLoading) {
    return <p className="text-muted-foreground text-sm">Loading transcript…</p>
  }
  if (query.isError) {
    return <p className="text-muted-foreground text-sm">Transcript could not be loaded</p>
  }
  return (
    <pre className="rounded bg-gray-50 p-3 text-xs whitespace-pre-wrap text-gray-800">
      {query.data?.transcript ?? ''}
    </pre>
  )
}

export function CallActivityCard({ leadId, activity }: Props) {
  const style = activityTypeStyles[activity.activityType]
  const Icon = style.icon
  const missed = isMissed(activity.activityType)
  const durationLabel = formatDuration(activity.callDurationSeconds)
  const detach = useDetachCallActivity(leadId)

  return (
    <Card>
      <CardContent>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('gap-1 px-2 py-0.5', style.className)}>
            <Icon className="size-3" />
            {style.label}
          </Badge>
          <span className="text-muted-foreground flex items-center text-sm">
            <ActivityAuthorIcon activity={activity} />
            <span>
              {activity.createdByName || 'System'} &middot; {formatDateTime(activity.createdAt)}
              {durationLabel && <> &middot; {durationLabel}</>}
            </span>
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Call activity actions" className="ml-auto">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() =>
                  detach.mutate(activity.id, {
                    onSuccess: () => toast.success('Detached from lead'),
                    onError: () => toast.error('Could not detach activity'),
                  })
                }
              >
                Detach from this lead
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {activity.notes && <p className="mt-2 text-sm">{activity.notes}</p>}

        {missed && (
          <p className="mt-2 text-sm text-orange-700">
            {activity.activityType === SalesLeadActivityType.CALL_INBOUND_MISSED
              ? 'Inbound call missed'
              : 'Outbound call not answered'}
          </p>
        )}

        {!missed && (
          <div className="mt-3 flex flex-col gap-2">
            <RecordingBlock activity={activity} />
            <TranscriptBlock activityId={activity.id} status={activity.transcriptStatus} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
